import type { FormConfig } from '../form-engine.js';
import { VERTICAL_KEYS } from '../vertical.js';

// =============================================================================
// Proteção veicular FormConfig — modelo enxuto pra Risco Zero.
//
// Diferente do MCMV, o PV não usa qualificação binária. Todo lead que chega
// aqui é elegível pra cotação; o resultado é uma mensagem pré-preenchida no
// WhatsApp do consultor. Por isso o form pede só o que é útil pra abrir a
// conversa direito: contato, identificação do veículo, cidade, tipo de
// cobertura desejada e urgência. Campos finos (km/mês, onde dorme, idade,
// sinistro, valor FIPE) saíram porque o consultor coleta isso depois no
// próprio WhatsApp se for relevante.
//
// 3 steps, 9 perguntas (versus os 4 steps + 13 do desenho anterior).
// =============================================================================

export const protecaoVeicularFormConfig: FormConfig = {
  version: 1,
  verticalKey: VERTICAL_KEYS.PROTECAO_VEICULAR,
  submitLabel: 'Enviar cotação no WhatsApp',
  steps: [
    {
      id: 'contact',
      title: 'Pra começar, seu contato',
      subtitle:
        'O consultor da Risco Zero vai retornar pelo WhatsApp com a cotação.',
      fields: [
        {
          id: 'name',
          scope: 'lead',
          type: 'text',
          label: 'Nome completo',
          placeholder: 'João Silva',
          validation: { required: true, minLength: 2, maxLength: 160 },
        },
        {
          id: 'email',
          scope: 'lead',
          type: 'email',
          label: 'E-mail',
          placeholder: 'joao@email.com',
          validation: { required: true },
        },
        {
          id: 'phone',
          scope: 'lead',
          type: 'phone',
          label: 'WhatsApp (com DDD)',
          placeholder: '(48) 99999-9999',
          validation: { required: true },
        },
      ],
    },
    {
      id: 'vehicle',
      title: 'Sobre o seu veículo',
      subtitle: 'Pra identificar o veículo na cotação.',
      fields: [
        {
          id: 'vehiclePlate',
          type: 'text',
          label: 'Placa do veículo',
          placeholder: 'ABC-1234',
          hint: 'Aceita placa antiga (ABC-1234) ou Mercosul (ABC1D23).',
          validation: { required: true, minLength: 7, maxLength: 8 },
        },
        {
          id: 'vehicleType',
          type: 'single-choice',
          label: 'Que tipo de veículo?',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'car', label: 'Carro', iconName: 'Car' },
            { value: 'motorcycle', label: 'Moto', iconName: 'Car' },
            { value: 'pickup', label: 'Caminhonete / SUV', iconName: 'Truck' },
            { value: 'truck', label: 'Caminhão / utilitário pesado', iconName: 'Truck' },
          ],
        },
        {
          id: 'vehicleYear',
          type: 'number',
          label: 'Ano do veículo',
          placeholder: '2018',
          validation: { required: true, min: 1980, max: 2030 },
        },
      ],
    },
    {
      id: 'context',
      title: 'Onde e quando',
      subtitle: 'Últimos detalhes pra adiantar a conversa.',
      fields: [
        {
          id: 'cityState',
          type: 'single-choice',
          label: 'Sua cidade',
          columns: 2,
          validation: { required: true },
          options: [
            { value: 'sao_jose', label: 'São José' },
            { value: 'florianopolis', label: 'Florianópolis' },
            { value: 'palhoca', label: 'Palhoça' },
            { value: 'biguacu', label: 'Biguaçu' },
            { value: 'kobrasol', label: 'Kobrasol' },
            { value: 'campinas', label: 'Campinas' },
            { value: 'barreiros', label: 'Barreiros' },
            { value: 'forquilhinhas', label: 'Forquilhinhas' },
            { value: 'other', label: 'Outra cidade' },
          ],
        },
        {
          id: 'cityOther',
          type: 'text',
          label: 'Qual cidade?',
          placeholder: 'Cidade · Estado',
          condition: { field: 'cityState', equals: 'other' },
          validation: { required: true, minLength: 2, maxLength: 80 },
        },
        {
          id: 'desiredPlan',
          type: 'single-choice',
          label: 'Que tipo de cobertura te interessa?',
          validation: { required: true },
          options: [
            {
              value: 'basic',
              label: 'Básica',
              description: 'Roubo, furto e perda total',
              iconName: 'ShieldCheck',
            },
            {
              value: 'intermediate',
              label: 'Intermediária',
              description: 'Básica + colisão e assistência 24h',
              iconName: 'ShieldCheck',
            },
            {
              value: 'complete',
              label: 'Completa',
              description: 'Tudo + carro reserva e cobertura a terceiros',
              iconName: 'ShieldCheck',
            },
            {
              value: 'not_sure',
              label: 'Não sei ainda',
              description: 'O consultor explica as opções',
              iconName: 'CircleHelp',
            },
          ],
        },
        {
          id: 'timeline',
          type: 'single-choice',
          label: 'Quando pretende contratar?',
          validation: { required: true },
          options: [
            { value: 'asap', label: 'Quero ativar agora', iconName: 'CalendarCheck2' },
            { value: 'this_week', label: 'Nos próximos dias', iconName: 'CalendarClock' },
            { value: 'this_month', label: 'Este mês', iconName: 'CalendarRange' },
            { value: 'just_quoting', label: 'Só pesquisando preço', iconName: 'CalendarDays' },
          ],
        },
      ],
    },
  ],
};
